/**
 * useEntitySearch — Spec 49 Phase 2b.
 *
 * React Query hook that fetches ranked search results for one of the three
 * entity types (template / workflow / employee). Debounces the query string
 * by `debounceMs` (default 250ms) before firing.
 *
 * Returns [] for an empty query string — callers don't need to guard for
 * undefined or handle a disabled query state.
 *
 * Each entity type is its own generated search operation (`searchAgentTemplates`,
 * `searchWorkflowDefinitions`, `searchWorkforce` / `searchDaemons`), so the generated
 * query key already carries the type, the query string and the limit: concurrent calls from
 * several search inputs (e.g., EntityPicker + list page) are deduplicated by React Query.
 */

import { useEffect, useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  searchAgentTemplatesOptions,
  searchDaemonsOptions,
  searchWorkflowDefinitionsOptions,
  searchWorkforceOptions,
} from '../generated/api/@tanstack/react-query.gen.js';
import type { EntitySearchRow, EntitySource, WorkforceSearchScope } from '../services/api/entity-search.api.js';

export type EntityType = 'template' | 'workflow' | 'employee';

export type EntitySearchResult = EntitySearchRow;

export interface UseEntitySearchOptions {
  /** Debounce delay in ms before the API is called. Default 250. */
  debounceMs?: number;
  /** Max results to request. Default 10. */
  limit?: number;
  /** Disable the hook entirely. Default true (enabled). */
  enabled?: boolean;
  /** Workforce/daemon route scope. Applies only to entityType='employee'. */
  workforceScope?: WorkforceSearchScope;
}

/** These two searches carry no slug (the row is addressed by id). */
function withIdSlug<T extends { id: number; name: string; score: number; source: EntitySource }>(
  rows: T[],
): EntitySearchResult[] {
  return rows.map((r) => ({ ...r, slug: String(r.id) }));
}

export function useEntitySearch(
  entityType: EntityType,
  query: string,
  opts?: UseEntitySearchOptions,
): UseQueryResult<EntitySearchResult[]> {
  const debounceMs = opts?.debounceMs ?? 250;
  const limit = opts?.limit ?? 10;
  const enabled = opts?.enabled ?? true;
  const workforceScope = opts?.workforceScope ?? 'org';

  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      // Clear immediately so the component resets without waiting for the timer.
      setDebouncedQuery('');
      return;
    }
    const id = setTimeout(() => setDebouncedQuery(trimmed), debounceMs);
    return () => clearTimeout(id);
  }, [query, debounceMs]);

  // Only fetch when there is a non-empty debounced query and the hook is enabled.
  const active = enabled && debouncedQuery.trim().length > 0;
  const searchParams = { query: { q: debouncedQuery, limit } };
  // One query per search operation, so each keeps its generated key and response type; only the active one fires.
  const shared = {
    // An empty answer as placeholder, so `select` hands callers `[]` until the first answer arrives.
    placeholderData: { data: [], query: '', total: 0, searchType: 'keyword' as const },
    staleTime: 30_000,
  };

  const templates = useQuery({
    ...searchAgentTemplatesOptions(searchParams),
    ...shared,
    enabled: active && entityType === 'template',
    select: (body): EntitySearchResult[] =>
      body.data.map((r) => ({
        id: r.id,
        slug: r.slug ?? String(r.id),
        name: r.name,
        description: r.description ?? null,
        department: r.departmentId ? String(r.departmentId) : null,
        category: r.category ?? null,
        score: r.score ?? 0,
        matchType: r.matchType,
        source: 'local',
        usageCount: r.usageCount,
        tags: r.tags,
      })),
  });
  const workflows = useQuery({
    ...searchWorkflowDefinitionsOptions(searchParams),
    ...shared,
    enabled: active && entityType === 'workflow',
    select: (body) => withIdSlug(body.data),
  });
  const org = useQuery({
    ...searchWorkforceOptions(searchParams),
    ...shared,
    enabled: active && entityType === 'employee' && workforceScope === 'org',
    select: (body) => withIdSlug(body.data),
  });
  const system = useQuery({
    ...searchDaemonsOptions(searchParams),
    ...shared,
    enabled: active && entityType === 'employee' && workforceScope === 'system',
    select: (body) => withIdSlug(body.data),
  });

  const result =
    entityType === 'template'
      ? templates
      : entityType === 'workflow'
        ? workflows
        : workforceScope === 'system'
          ? system
          : org;
  if (result.isError) console.error(`useEntitySearch: failed to search ${entityType}`, result.error);
  return result;
}
